import React, { useState } from "react";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import Typography from "@mui/material/Typography";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import { StoreState } from "../../redux/configure-store";
import PageTitle from "../components/PageTitle";
import ListHeader from "../../common/components/list-header";
import { formatShortDate } from "../../common/utils";
import { markInvoiceAsPaid } from "../admin-actions";
import api from "../../api/api";
import { Invoice, PaymentMethod } from "../types";
import { borders } from "../../theme/theme";
import { stringSortCompare } from "../../common/utils";
import { routePaths, setPathId } from "../../routing/route-paths";
import { backgrounds } from "../../theme/theme";

type PaymentMethodStats = {
  paymentMethod: PaymentMethod;
  totalAmountOwed: number;
  numberOfOpenInvoices: number;
};

function AdminInvoicesTable(props: any) {
  const { markInvoiceAsPaid } = props;

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [sortColumnName, setSortColumnName] = useState("Payment Due At");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [hidePaidInvoices, setHidePaidInvoices] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const [dateField, setDateField] = useState<
    "createdAt" | "periodStartDate" | "periodEndDate"
  >("createdAt");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedClient, setSelectedClient] = useState("all");
  const [selectedDeveloper, setSelectedDeveloper] = useState("all");
  const [exportFormat, setExportFormat] = useState<"combined" | "zip">(
    "zip"
  );
  const [exporting, setExporting] = useState(false);

  const paymentMethodLabels: Record<PaymentMethod, string> = {
    bmoUsd: "BMO USD",
    bitcoin: "Bitcoin",
    wise: "Wise",
    stablecoin: "Stablecoin",
  };

  const defaultPaymentMethodStats: PaymentMethodStats = {
    paymentMethod: "wise",
    totalAmountOwed: 0,
    numberOfOpenInvoices: 0,
  };
  const [numberOfOpenInvoices, setNumberOfOpenInvoices] = useState(0);
  const [totalAmountOwed, setTotalAmountOwed] = useState(0);
  const [bmoUsdStats, setBmoUsdStats] = useState<PaymentMethodStats>(
    defaultPaymentMethodStats
  );
  const [bitcoinStats, setBitcoinStats] = useState<PaymentMethodStats>(
    defaultPaymentMethodStats
  );
  const [wiseStats, setWiseStats] = useState<PaymentMethodStats>(
    defaultPaymentMethodStats
  );
  const [stablecoinStats, setStablecoinStats] = useState<PaymentMethodStats>(
    defaultPaymentMethodStats
  );

  function filterOpenInvoices(invoice: Invoice) {
    return !invoice.paidAt;
  }

  function filterPaymentMethod(paymentMethod: PaymentMethod) {
    return function (invoice: Invoice) {
      return (invoice.paymentMethod || invoice.user.paymentMethod) === paymentMethod;
    };
  }

  function reduceInvoicePaymentAmount(acc: number, invoice: Invoice) {
    return invoice.totalPaymentAmount + acc;
  }

  function sortInvoiceList(list: Invoice[], columnName: string, direction: "asc" | "desc"): Invoice[] {
    const dir = direction === "asc" ? 1 : -1;
    return [...list].sort((a: any, b: any) => {
      switch (columnName) {
        case "Developer Name":
          return dir * stringSortCompare(a.user.firstName, b.user.firstName);
        case "Client Name":
          return dir * stringSortCompare(a.engagement.clientName, b.engagement.clientName);
        case "Payment Due At":
          return dir * (new Date(a.paymentDueAt).getTime() - new Date(b.paymentDueAt).getTime());
        case "Created At":
          return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        case "Payment Method": {
          const aM = a.paymentMethod || a.user.paymentMethod || "";
          const bM = b.paymentMethod || b.user.paymentMethod || "";
          return dir * stringSortCompare(aM, bM);
        }
        case "Paid At":
          return dir * (new Date(a.paidAt).getTime() - new Date(b.paidAt).getTime());
        default:
          return 0;
      }
    });
  }

  React.useEffect(() => {
    setInvoices(sortInvoiceList(props.invoices || [], sortColumnName, sortDirection));
  }, [props.invoices]);

  React.useEffect(() => {
    if (!invoices || !invoices.length) {
      return;
    }
    const openInvoices = invoices.filter(filterOpenInvoices);
    setNumberOfOpenInvoices(openInvoices.length);

    setTotalAmountOwed(openInvoices.reduce(reduceInvoicePaymentAmount, 0));

    // open invoices by payment amount
    setBmoUsdStats({
      paymentMethod: "bmoUsd",
      totalAmountOwed: openInvoices
        .filter(filterPaymentMethod("bmoUsd"))
        .reduce(reduceInvoicePaymentAmount, 0),
      numberOfOpenInvoices: openInvoices.filter(filterPaymentMethod("bmoUsd"))
        .length,
    });

    setWiseStats({
      paymentMethod: "wise",
      totalAmountOwed: openInvoices
        .filter(filterPaymentMethod("wise"))
        .reduce(reduceInvoicePaymentAmount, 0),
      numberOfOpenInvoices: openInvoices.filter(filterPaymentMethod("wise"))
        .length,
    });

    setBitcoinStats({
      paymentMethod: "bitcoin",
      totalAmountOwed: openInvoices
        .filter(filterPaymentMethod("bitcoin"))
        .reduce(reduceInvoicePaymentAmount, 0),
      numberOfOpenInvoices: openInvoices.filter(filterPaymentMethod("bitcoin"))
        .length,
    });

    setStablecoinStats({
      paymentMethod: "stablecoin",
      totalAmountOwed: openInvoices
        .filter(filterPaymentMethod("stablecoin"))
        .reduce(reduceInvoicePaymentAmount, 0),
      numberOfOpenInvoices: openInvoices.filter(
        filterPaymentMethod("stablecoin")
      ).length,
    });
  }, [invoices]);

  const columnNames = [
    "id",
    { name: "Developer Name", isSortable: true },
    { name: "Client Name", isSortable: true },
    "Total Number of Hours",
    "Total Payment Amount",
    "Currency",
    "Period Start Date",
    "Period End Date",
    { name: "Created At", isSortable: true },
    { name: "Payment Method", isSortable: true },
    { name: "Payment Due At", isSortable: true },
    { name: "Paid At", isSortable: true },
  ];

  function onRowClick(publicId: string) {
    window.open(
      setPathId(routePaths.invoices.invoicesView, publicId),
      "_blank"
    );
  }

  const changeSort = (columnName: string) => {
    let newSortDirection: "asc" | "desc" = "desc";
    if (columnName === sortColumnName) {
      newSortDirection = sortDirection === "desc" ? "asc" : "desc";
    }
    setSortDirection(newSortDirection);
    setSortColumnName(columnName);
    setInvoices(sortInvoiceList(invoices, columnName, newSortDirection));
  };

  function passesFilters(invoice: Invoice): boolean {
    if (hidePaidInvoices && invoice.paidAt) {
      return false;
    }
    if (
      selectedPaymentMethod &&
      (invoice.paymentMethod || invoice.user.paymentMethod) !==
        selectedPaymentMethod
    ) {
      return false;
    }
    if (
      selectedClient !== "all" &&
      invoice.engagement.clientName !== selectedClient
    ) {
      return false;
    }
    if (
      selectedDeveloper !== "all" &&
      String(invoice.user.id) !== selectedDeveloper
    ) {
      return false;
    }
    if (startDate || endDate) {
      const dateStr = formatShortDate((invoice as any)[dateField]);
      if (!dateStr) {
        return false;
      }
      if (startDate && dateStr < startDate) {
        return false;
      }
      if (endDate && dateStr > endDate) {
        return false;
      }
    }
    return true;
  }

  const allInvoices = (invoices || []) as Invoice[];
  const filteredInvoices = allInvoices.filter(passesFilters);

  const clientOptions = Array.from(
    new Set(allInvoices.map((invoice) => invoice.engagement.clientName))
  ).sort();

  const developerOptions = Array.from(
    new Map(
      allInvoices.map((invoice) => [
        String(invoice.user.id),
        `${invoice.user.firstName} ${invoice.user.lastName}`,
      ])
    ).entries()
  ).sort((a, b) => stringSortCompare(a[1], b[1]));

  const handleExport = async () => {
    if (!filteredInvoices.length) {
      return;
    }
    setExporting(true);
    try {
      const blob = await api.admin.exportInvoices(
        filteredInvoices.map((invoice) => invoice.publicId),
        exportFormat
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = exportFormat === "zip" ? "invoices.zip" : "invoices.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Error exporting invoices");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageTitle>Invoices</PageTitle>
      <Box sx={{ mb: 3, p: 2, backgroundColor: backgrounds.page, borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2">
              <strong>Total Owed:</strong> {totalAmountOwed}
            </Typography>
            <Typography variant="body2">
              <strong>Open Invoices:</strong> {numberOfOpenInvoices}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2">
              <strong>BMO USD:</strong> {bmoUsdStats.totalAmountOwed} ({bmoUsdStats.numberOfOpenInvoices} open)
            </Typography>
            <Typography variant="body2">
              <strong>Bitcoin:</strong> {bitcoinStats.totalAmountOwed} ({bitcoinStats.numberOfOpenInvoices} open)
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2">
              <strong>Stablecoin:</strong> {stablecoinStats.totalAmountOwed} ({stablecoinStats.numberOfOpenInvoices} open)
            </Typography>
            <Typography variant="body2">
              <strong>Wise:</strong> {wiseStats.totalAmountOwed} ({wiseStats.numberOfOpenInvoices} open)
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={hidePaidInvoices}
              onChange={(e) => setHidePaidInvoices(e.target.checked)}
            />
          }
          label="Hide paid invoices"
          sx={{ mr: 2 }}
        />
        {(["bmoUsd", "bitcoin", "wise", "stablecoin"] as PaymentMethod[]).map((method) => (
          <Chip
            key={method}
            label={paymentMethodLabels[method]}
            onClick={() => setSelectedPaymentMethod(selectedPaymentMethod === method ? null : method)}
            variant={selectedPaymentMethod === method ? "filled" : "outlined"}
            color={selectedPaymentMethod === method ? "primary" : "default"}
            size="small"
            sx={{ cursor: "pointer" }}
          />
        ))}
      </Box>
      <Box
        sx={{
          mb: 2,
          p: 2,
          display: "flex",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: backgrounds.page,
          borderRadius: 1,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date field</InputLabel>
          <Select
            label="Date field"
            value={dateField}
            onChange={(e) => setDateField(e.target.value as any)}
          >
            <MenuItem value="createdAt">Created date</MenuItem>
            <MenuItem value="periodStartDate">Period start date</MenuItem>
            <MenuItem value="periodEndDate">Period end date</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Client</InputLabel>
          <Select
            label="Client"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            <MenuItem value="all">All clients</MenuItem>
            {clientOptions.map((clientName) => (
              <MenuItem key={clientName} value={clientName}>
                {clientName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Developer</InputLabel>
          <Select
            label="Developer"
            value={selectedDeveloper}
            onChange={(e) => setSelectedDeveloper(e.target.value)}
          >
            <MenuItem value="all">All developers</MenuItem>
            {developerOptions.map(([userId, name]) => (
              <MenuItem key={userId} value={userId}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {(startDate ||
          endDate ||
          selectedClient !== "all" ||
          selectedDeveloper !== "all") && (
          <Button
            size="small"
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setSelectedClient("all");
              setSelectedDeveloper("all");
            }}
          >
            Clear
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Export format</InputLabel>
          <Select
            label="Export format"
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as any)}
          >
            <MenuItem value="combined">Single combined PDF</MenuItem>
            <MenuItem value="zip">Separate PDFs (ZIP)</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          disabled={exporting || filteredInvoices.length === 0}
          onClick={handleExport}
        >
          {exporting
            ? "Exporting…"
            : `Export ${filteredInvoices.length} as PDF`}
        </Button>
      </Box>
      <Box>
        <Paper elevation={0} sx={{ width: "100%", overflowX: "auto", border: `1px solid ${borders.light}`, borderRadius: 2 }}>
          <Table sx={{ minWidth: 700, mb: 5 }}>
            <ListHeader
              columns={columnNames}
              sortDirection={sortDirection}
              sortColumnName={sortColumnName}
              onColumnClick={(columnName: string) => changeSort(columnName)}
            />
            <TableBody>
              {filteredInvoices
                .map((invoice) => {
                return (
                  <TableRow
                    sx={{ cursor: "pointer" }}
                    key={invoice.publicId}
                    onClick={() => onRowClick(invoice.publicId)}
                  >
                    <TableCell component="th" scope="row">
                      {invoice.publicId}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {invoice.user.firstName} {invoice.user.lastName}
                    </TableCell>
                    <TableCell>{invoice.engagement.clientName}</TableCell>
                    <TableCell>{invoice.totalNumberOfHours}</TableCell>
                    <TableCell
                      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(String(invoice.totalPaymentAmount)).then(() => setCopySnackbarOpen(true)); }}
                      sx={{
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                        "&:active": { backgroundColor: "rgba(0, 92, 255, 0.1)" },
                      }}
                    >
                      {invoice.totalPaymentAmount}
                    </TableCell>
                    <TableCell>{invoice.currency}</TableCell>

                    <TableCell>
                      {formatShortDate(invoice.periodStartDate)}
                    </TableCell>
                    <TableCell>
                      {formatShortDate(invoice.periodEndDate)}
                    </TableCell>

                    <TableCell>{formatShortDate(invoice.createdAt)}</TableCell>
                    <TableCell>{invoice.paymentMethod || invoice.user.paymentMethod}</TableCell>
                    <TableCell>
                      {formatShortDate(invoice.paymentDueAt)}
                    </TableCell>
                    <TableCell>
                      {invoice.paidAt ? (
                        formatShortDate(invoice.paidAt)
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            markInvoiceAsPaid(invoice.publicId);
                          }}
                        >
                          Mark as Paid
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Box>
      <Snackbar
        open={copySnackbarOpen}
        onClose={() => setCopySnackbarOpen(false)}
        autoHideDuration={2000}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </div>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  invoices: storeState.admin.invoices,
});

export default connect(mapStateToProps, { markInvoiceAsPaid })(
  AdminInvoicesTable
);
